/* Matres cart engine — port of src/stores/cartStore.ts + src/pages/Cart.tsx behaviour
 * onto the native Shopify AJAX Cart API. Much simpler than the headless app:
 * no custom note format, no bundle billing — plain variant lines.
 *   - addItem / changeLine (Section Rendering re-render on the cart page)
 *   - buyer note -> cart.note (debounced, like cartNoteUpdate)
 *   - discount codes via /cart/update.js { discount } (Cart AJAX API, 2025-05+)
 *   - free-shipping progress bar (threshold/fee from window.MATRES)
 *   - toast on add, cart badges, pixels
 * Mutations are serialized through a queue so they never race.
 */
(function () {
  'use strict';

  var M = window.MATRES || {};
  var LOCALE = M.locale === 'ar' ? 'ar' : 'en';
  var ROUTES = M.routes || {};
  var CURRENCY = M.currency || 'QAR';
  var THRESHOLD = Number(M.freeShippingThreshold != null ? M.freeShippingThreshold : 500);
  var SHIP_FEE = Number(M.shippingFee != null ? M.shippingFee : 35);

  function t(key) {
    // strings come from the theme locale files via window.MATRES.strings
    var S = M.strings || {};
    var map = {
      added_to_cart: S.addedToCart, view_cart: S.viewCart, item_one: S.itemOne, item_many: S.itemMany,
      free_unlocked: S.freeUnlocked, add_more_prefix: S.addMorePrefix, add_more_suffix: S.addMoreSuffix,
      invalid_code: S.invalidCode, code_applied: S.codeApplied, saved: S.saved
    };
    var fallback = {
      added_to_cart: 'Added to cart', view_cart: 'View cart', item_one: 'item', item_many: 'items',
      free_unlocked: 'Free shipping unlocked', add_more_prefix: 'Add ', add_more_suffix: ' more for free shipping',
      invalid_code: 'Invalid discount code', code_applied: 'Discount applied', saved: 'Saved'
    };
    return map[key] != null ? map[key] : fallback[key];
  }

  /* Display money — matches the Lovable formatPrice: "QAR 199.00" (both locales). */
  function money(major) {
    var n = Number(major);
    if (isNaN(n)) n = 0;
    return CURRENCY + ' ' + n.toFixed(2);
  }
  function moneyCents(cents) { return money((Number(cents) || 0) / 100); }
  window.MatresMoney = moneyCents;
  window.MatresMoneyMajor = money;

  // ── AJAX (JSON bodies) ─────────────────────────────────────────────────────
  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body || {})
    }).then(function (r) {
      return r.json().then(function (data) {
        if (!r.ok) {
          var e = new Error((data && (data.description || data.message)) || ('HTTP ' + r.status));
          e.data = data; e.status = r.status;
          throw e;
        }
        return data;
      });
    });
  }
  function getCart() {
    return fetch((ROUTES.cart || '/cart') + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  // Serialize mutations.
  var queue = Promise.resolve();
  function enqueue(task) {
    var run = queue.then(task, task);
    queue = run.catch(function () {});
    return run;
  }

  // ── UI sync ────────────────────────────────────────────────────────────────
  function syncUI(cart) {
    var n = cart.item_count || 0;
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = n;
      el.hidden = n === 0;
    });
    updateFreeShipBar(cart);
    try { document.dispatchEvent(new CustomEvent('matres:cart', { detail: { cart: cart } })); } catch (e) {}
  }

  function updateFreeShipBar(cart) {
    var bar = document.querySelector('[data-freeship]');
    if (!bar) return;
    var subtotal = (cart.items_subtotal_price != null ? cart.items_subtotal_price : cart.total_price || 0) / 100;
    var qualifies = subtotal >= THRESHOLD;
    var remaining = Math.max(0, THRESHOLD - subtotal);
    var pct = THRESHOLD > 0 ? Math.min(100, Math.round((subtotal / THRESHOLD) * 100)) : 100;
    var fill = bar.querySelector('[data-freeship-fill]');
    var text = bar.querySelector('[data-freeship-text]');
    if (fill) {
      fill.style.width = pct + '%';
      fill.style.backgroundColor = qualifies ? '#3FAE74' : 'rgb(83,66,48)';
    }
    if (text) {
      text.textContent = qualifies
        ? t('free_unlocked')
        : t('add_more_prefix') + money(remaining) + t('add_more_suffix');
    }
  }

  // ── toast ──────────────────────────────────────────────────────────────────
  function toastContainer() {
    var el = document.getElementById('matres-toasts');
    if (!el) {
      el = document.createElement('div');
      el.id = 'matres-toasts';
      el.setAttribute('aria-live', 'polite');
      el.className = 'pointer-events-none fixed inset-x-0 bottom-0 z-[100] flex flex-col items-center gap-2 px-4 pb-[calc(env(safe-area-inset-bottom,0px)+5.5rem)]';
      document.body.appendChild(el);
    }
    return el;
  }
  function esc(s) { return String(s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }

  function showToast(o) {
    var dir = LOCALE === 'ar' ? 'rtl' : 'ltr';
    var unit = o.quantity === 1 ? t('item_one') : t('item_many');
    var card = document.createElement('div');
    card.dir = dir;
    card.className = 'pointer-events-auto w-[min(92vw,360px)] overflow-hidden rounded-[14px] border bg-white shadow-xl';
    card.style.borderColor = 'rgb(238,238,238)';
    card.innerHTML =
      '<div class="flex items-center gap-2 px-4 py-2" style="background:#ECF9F2;color:#2E8B57;">' +
        '<span class="grid h-5 w-5 place-items-center rounded-full text-white" style="background:#3FAE74;">✓</span>' +
        '<span class="text-xs font-semibold tracking-wide">' + esc(t('added_to_cart')) + '</span>' +
      '</div>' +
      '<div class="flex gap-3 p-3">' +
        (o.image ? '<img src="' + esc(o.image) + '" alt="" class="h-16 w-16 flex-shrink-0 rounded-[10px] object-cover">' : '') +
        '<div class="min-w-0 flex-1">' +
          '<p class="truncate text-sm font-medium" style="color:rgb(83,66,48);">' + esc(o.title || '') + '</p>' +
          (o.variant ? '<p class="truncate text-[12px]" style="color:rgb(102,102,102);">' + esc(o.variant) + (o.quantity > 1 ? ' · ' + o.quantity + ' ' + unit : '') + '</p>' : '') +
          (isFinite(o.price) ? '<div class="mt-1.5 text-sm font-semibold" style="color:rgb(83,66,48);">' + esc(money(o.price)) + '</div>' : '') +
        '</div>' +
      '</div>' +
      '<button type="button" class="m-btn" style="display:flex;width:100%;border-radius:0;">' + esc(t('view_cart')) + '</button>';
    card.querySelector('button').addEventListener('click', function () { window.location.href = ROUTES.cart || '/cart'; });
    toastContainer().appendChild(card);
    var timer = setTimeout(remove, 4500);
    card.addEventListener('mouseenter', function () { clearTimeout(timer); });
    card.addEventListener('mouseleave', function () { timer = setTimeout(remove, 1500); });
    function remove() { card.style.transition = 'opacity .25s'; card.style.opacity = '0'; setTimeout(function () { card.remove(); }, 250); }
  }

  function showError(msg) {
    var card = document.createElement('div');
    card.dir = LOCALE === 'ar' ? 'rtl' : 'ltr';
    card.className = 'pointer-events-auto w-[min(92vw,360px)] rounded-[14px] border bg-white px-4 py-3 text-sm font-medium shadow-xl';
    card.style.borderColor = 'rgba(232,78,78,.4)';
    card.style.color = '#E84E4E';
    card.setAttribute('role', 'alert');
    card.textContent = msg;
    toastContainer().appendChild(card);
    setTimeout(function () { card.style.transition = 'opacity .25s'; card.style.opacity = '0'; setTimeout(function () { card.remove(); }, 250); }, 3500);
  }

  // ── section rendering ──────────────────────────────────────────────────────
  function renderSection(id, html) {
    var target = document.getElementById('shopify-section-' + id);
    if (!target) return;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var fresh = doc.getElementById('shopify-section-' + id);
    target.innerHTML = fresh ? fresh.innerHTML : html;
  }

  function idOf(v) { // accept numeric id or GID
    var s = String(v);
    var m = s.match(/(\d+)\s*$/);
    return m ? m[1] : s;
  }

  // ── public API ─────────────────────────────────────────────────────────────
  /* line = { variantId, quantity, title, variant, image, price (major, unit*qty), name } */
  function addItem(line) {
    return enqueue(function () {
      return postJSON(ROUTES.cartAdd || '/cart/add.js', { items: [{ id: Number(idOf(line.variantId)), quantity: line.quantity || 1 }] })
        .then(getCart)
        .then(function (cart) {
          syncUI(cart);
          if (!line.silent) {
            showToast({ image: line.image, title: line.title, variant: line.variant, quantity: line.quantity || 1, price: line.price });
          }
          if (window.MatresPixels) {
            window.MatresPixels.addToCart({
              contentName: line.name || line.title,
              items: [{ id: idOf(line.variantId), quantity: line.quantity || 1, price: line.price / (line.quantity || 1), name: line.name || line.title }],
              value: line.price
            });
          }
          return cart;
        });
    });
  }

  /* items = [{ variantId, quantity }] — multi-line add (four-toppers bundle). */
  function addItems(items) {
    return enqueue(function () {
      var payload = items.map(function (i) { return { id: Number(idOf(i.variantId)), quantity: i.quantity || 1 }; });
      return postJSON(ROUTES.cartAdd || '/cart/add.js', { items: payload })
        .then(getCart)
        .then(function (cart) { syncUI(cart); return cart; });
    });
  }

  function changeLine(key, quantity, sectionId) {
    return enqueue(function () {
      var body = { id: key, quantity: quantity };
      if (sectionId) { body.sections = sectionId; body.sections_url = window.location.pathname; }
      return postJSON(ROUTES.cartChange || '/cart/change.js', body).then(function (res) {
        if (sectionId && res.sections && res.sections[sectionId]) renderSection(sectionId, res.sections[sectionId]);
        return getCart().then(function (cart) { syncUI(cart); return cart; });
      });
    });
  }

  /* Buyer note → cart.note (matches the app's cartNoteUpdate). */
  function setNote(text) {
    return enqueue(function () {
      return postJSON(ROUTES.cartUpdate || '/cart/update.js', { note: text == null ? '' : String(text).slice(0, 1000) });
    });
  }

  /* Discount codes via Cart AJAX API (update.js `discount` param).
   * The param REPLACES the cart's whole code list, so on an invalid code we
   * roll back to the codes that were applied before (not to empty), and we
   * re-render so the UI never keeps showing a discount that was lost. */
  function applyDiscount(code, sectionId) {
    return enqueue(function () {
      return getCart().then(function (before) {
        var previous = (before.discount_codes || [])
          .filter(function (c) { return c.applicable !== false; })
          .map(function (c) { return c.code; });
        var wanted = String(code || '').trim();
        var combined = previous.filter(function (c) { return c.toLowerCase() !== wanted.toLowerCase(); });
        combined.push(wanted);
        var body = { discount: combined.join(',') };
        if (sectionId) { body.sections = sectionId; body.sections_url = window.location.pathname; }
        return postJSON(ROUTES.cartUpdate || '/cart/update.js', body).then(function (res) {
          var codes = res.discount_codes || [];
          var hit = codes.filter(function (c) { return String(c.code).toLowerCase() === wanted.toLowerCase(); })[0];
          var ok = !!(hit && hit.applicable !== false);
          if (!ok) {
            // restore exactly what was applied before the attempt
            var rollback = { discount: previous.join(',') };
            if (sectionId) { rollback.sections = sectionId; rollback.sections_url = window.location.pathname; }
            return postJSON(ROUTES.cartUpdate || '/cart/update.js', rollback).then(function (res2) {
              if (sectionId && res2.sections && res2.sections[sectionId]) renderSection(sectionId, res2.sections[sectionId]);
              return getCart().then(function (cart) {
                syncUI(cart);
                var err = new Error(t('invalid_code'));
                err.invalidCode = true;
                throw err;
              });
            });
          }
          if (sectionId && res.sections && res.sections[sectionId]) renderSection(sectionId, res.sections[sectionId]);
          return getCart().then(function (cart) { syncUI(cart); return cart; });
        });
      });
    });
  }
  function removeDiscount(sectionId) {
    return enqueue(function () {
      var body = { discount: '' };
      if (sectionId) { body.sections = sectionId; body.sections_url = window.location.pathname; }
      return postJSON(ROUTES.cartUpdate || '/cart/update.js', body).then(function (res) {
        if (sectionId && res.sections && res.sections[sectionId]) renderSection(sectionId, res.sections[sectionId]);
        return getCart().then(function (cart) { syncUI(cart); return cart; });
      });
    });
  }

  function checkout() {
    return enqueue(function () { return getCart(); }).then(function (cart) {
      if (window.MatresPixels) {
        var items = (cart.items || []).map(function (it) {
          return { id: String(it.variant_id), quantity: it.quantity, price: it.price / 100, name: it.product_title };
        });
        window.MatresPixels.initiateCheckout({ items: items, value: (cart.total_price || 0) / 100 });
      }
      window.location.href = '/checkout';
    });
  }

  window.MatresCart = {
    addItem: addItem,
    addItems: addItems,
    changeLine: changeLine,
    setNote: setNote,
    applyDiscount: applyDiscount,
    removeDiscount: removeDiscount,
    checkout: checkout,
    getCart: getCart,
    money: money,
    moneyCents: moneyCents,
    toast: showToast,
    toastError: showError,
    refresh: function () { return getCart().then(function (c) { syncUI(c); return c; }); }
  };

  // ── cart-page bindings (delegated: survive Section Rendering re-renders) ────
  // JSON templates get dynamic section IDs, so read it off the section root.
  function cartSectionId() {
    var el = document.querySelector('[data-cart-section]');
    return el ? el.getAttribute('data-section-id') : null;
  }

  // Re-fetch + re-render the cart section from the server. Used after a 422 so the
  // shown quantity settles to the real, stock-clamped value instead of the stale
  // optimistic number the failed request left behind.
  function resyncCartSection() {
    var id = cartSectionId();
    if (!id) return Promise.resolve();
    return fetch((ROUTES.cart || '/cart') + '?sections=' + encodeURIComponent(id), { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (data) { if (data && data[id]) renderSection(id, data[id]); })
      .catch(function () {});
  }

  // ── Quantity updates: optimistic + debounced + serialized ──────────────────
  // The input value is written instantly (snappy feel); the actual
  // /cart/change.js is DEBOUNCED so rapid +/- clicks coalesce into ONE request,
  // and SERIALIZED (only one in flight at a time) so concurrent changes can't
  // race into an HTTP 422 / stale re-render. A line changed while a request was
  // in flight is flushed right after it resolves.
  var qtyDesired = {};
  var qtyDebounce = null;
  var qtyBusy = false;
  function queueQty(key, qty) {
    qtyDesired[key] = qty;
    clearTimeout(qtyDebounce);
    qtyDebounce = setTimeout(flushQty, 350);
  }
  function flushQty() {
    if (qtyBusy) return;
    var keys = Object.keys(qtyDesired);
    if (!keys.length) return;
    var key = keys[0];
    var qty = qtyDesired[key];
    delete qtyDesired[key];
    qtyBusy = true;
    changeLine(key, qty, cartSectionId())
      .catch(function (err) { showError(err.message); resyncCartSection(); })
      .finally(function () {
        qtyBusy = false;
        if (Object.keys(qtyDesired).length) flushQty();
      });
  }

  document.addEventListener('click', function (e) {
    var target = e.target;

    var qtyBtn = target.closest && target.closest('[data-qty-change]');
    if (qtyBtn) {
      e.preventDefault();
      var key = qtyBtn.getAttribute('data-line-key');
      var delta = Number(qtyBtn.getAttribute('data-qty-change'));
      // Each line renders two inputs (mobile + desktop). Read the one next to
      // the clicked button, then write the new value into BOTH optimistically
      // so rapid clicks and desktop/mobile edits never read stale values.
      var inputs = document.querySelectorAll('[data-qty-input][data-line-key="' + CSS.escape(key) + '"]');
      var local = qtyBtn.parentNode && qtyBtn.parentNode.querySelector('[data-qty-input]');
      var current = local ? Number(local.value) || 1 : (inputs[0] ? Number(inputs[0].value) || 1 : 1);
      var next = Math.max(0, current + delta);
      // Cap at the line's available stock so we never over-request → avoids the
      // /cart/change.js 422 ("Only N items … due to availability").
      var maxAttr = (local && local.getAttribute('data-qty-max')) || (inputs[0] && inputs[0].getAttribute('data-qty-max'));
      var lineMax = Number(maxAttr) || 0;
      if (lineMax > 0 && next > lineMax) next = lineMax;
      if (next === current) return;
      inputs.forEach(function (inp) { inp.value = next; });
      queueQty(key, next);
      return;
    }

    var removeBtn = target.closest && target.closest('[data-line-remove]');
    if (removeBtn) {
      e.preventDefault();
      queueQty(removeBtn.getAttribute('data-line-key'), 0);
      return;
    }

    var couponBtn = target.closest && target.closest('[data-coupon-apply]');
    if (couponBtn) {
      e.preventDefault();
      var couponInput = document.querySelector('[data-coupon-input]');
      var code = couponInput ? couponInput.value.trim() : '';
      if (!code) return;
      couponBtn.disabled = true;
      applyDiscount(code, cartSectionId())
        .then(function () { showToast({ title: t('code_applied'), variant: code.toUpperCase(), quantity: 1, price: NaN }); })
        .catch(function (err) { showError(err.invalidCode ? err.message : (err.message || t('invalid_code'))); })
        .finally(function () { couponBtn.disabled = false; });
      return;
    }

    var couponRemove = target.closest && target.closest('[data-coupon-remove]');
    if (couponRemove) {
      e.preventDefault();
      removeDiscount(cartSectionId()).catch(function (err) { showError(err.message); });
      return;
    }

    var checkoutBtn = target.closest && target.closest('[data-checkout]');
    if (checkoutBtn) {
      e.preventDefault();
      checkoutBtn.disabled = true;
      checkout();
      return;
    }
  });

  document.addEventListener('change', function (e) {
    var input = e.target && e.target.closest && e.target.closest('[data-qty-input]');
    if (input) {
      var qty = Math.max(0, Number(input.value) || 0);
      var imax = Number(input.getAttribute('data-qty-max')) || 0;
      if (imax > 0 && qty > imax) qty = imax;
      var ikey = input.getAttribute('data-line-key');
      document.querySelectorAll('[data-qty-input][data-line-key="' + CSS.escape(ikey) + '"]').forEach(function (inp) { inp.value = qty; });
      queueQty(ikey, qty);
    }
  });

  // Buyer note (debounced 600ms like the app)
  var noteTimer = null;
  document.addEventListener('input', function (e) {
    var noteEl = e.target && e.target.closest && e.target.closest('[data-cart-note]');
    if (!noteEl) return;
    clearTimeout(noteTimer);
    noteTimer = setTimeout(function () {
      setNote(noteEl.value).then(function () {
        var s = document.querySelector('[data-cart-note-status]');
        if (s) { s.textContent = t('saved'); setTimeout(function () { s.textContent = ''; }, 2000); }
      });
    }, 600);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { window.MatresCart.refresh(); }, { once: true });
  } else {
    window.MatresCart.refresh();
  }

  // bfcache: pages restored via browser Back keep disabled buttons — re-enable.
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    document.querySelectorAll('[data-checkout],[data-coupon-apply],[data-card-add],[data-pdp-add],[data-pdp-buy-now],[data-bundle-add]').forEach(function (btn) {
      btn.disabled = false;
      btn.style.opacity = '';
    });
    window.MatresCart.refresh();
  });
})();
